// Typed client for the admin API (/admin-api). Auth is handled by the proxy:
// Traefik forward-auth to Authentik adds the signed JWT the API verifies.

export type Severity = 'error' | 'warning'

export interface Finding {
  severity: Severity
  code: string
  message: string
  source: string | null
}

export interface Findings {
  errors: Finding[]
  warnings: Finding[]
}

export interface Me {
  sub: string
  name: string
  email: string
  groups: string[]
  roles: string[]
  /** The org the requests act in. */
  org_id: string
  /** A platform super-user (aleva-admin): may act in any org. */
  super: boolean
  /** Logged in on app.aleva.se (not through Authentik). */
  customer: boolean
  /** saas (aleva.se) or enterprise. */
  edition?: string
  licence?: LicenceStatus
}

export interface LicenceStatus {
  state: 'not_required' | 'valid' | 'expiring' | 'grace' | 'expired' | 'missing' | 'invalid'
  licence?: { customer: string; edition: string; expires_at: string; limits: { orgs?: number; users?: number } }
  days_left: number
  message?: string
}

export interface Org {
  id: string
  name: string
  platform: boolean
  created_by: string
  created_at: string
  disabled_at: string | null
  members: number
}

export interface Membership {
  org_id: string
  org_name: string
  role: string
}

export interface Member {
  user_id: number
  email: string
  name: string
  role: 'admin' | 'editor' | 'viewer'
  created_at: string
  last_login_at: string | null
}

export interface Invitation {
  id: number
  org_id: string
  email: string
  role: string
  invited_by: string
  created_at: string
  expires_at: string
  accepted_at: string | null
  revoked_at: string | null
}

/** The org requests act in (super-users and members of several orgs pick it). */
const ORG_KEY = 'aleva-org'
export const activeOrg = {
  get: (): string => {
    try {
      return localStorage.getItem(ORG_KEY) ?? ''
    } catch {
      return ''
    }
  },
  set: (org: string) => {
    try {
      if (org) localStorage.setItem(ORG_KEY, org)
      else localStorage.removeItem(ORG_KEY)
    } catch {
      /* private mode */
    }
  },
}

export interface LicenseSummary {
  version: number
  name: string
  spdx_id: string | null
  checked_at: string | null
  complete: boolean
}

export interface SourceRow {
  slug: string
  name: string
  publisher: string
  access_method: string
  usage_mode: string
  status: string
  schedule: string | null
  next_run: string | null
  last_run_at: string | null
  last_run_status: string | null
  license: LicenseSummary | null
  license_errors: number
  license_warnings: number
  active_run: Run | null
}

export interface License {
  version: number
  name: string
  spdx_id: string | null
  commercial_use: boolean | null
  redistribution: boolean | null
  share_alike: boolean
  caching_limit_seconds: number | null
  attribution_text: string | null
  /** false: published without attribution or licence mention (a recorded decision). */
  attribution_required: boolean
  terms_url: string | null
  terms_sha256: string | null
  terms_snapshot_key: string | null
  checked_at: string | null
  checked_by: string | null
  notes: string
  created_at: string
  created_by: string
}

export type LicenseInput = Partial<
  Pick<
    License,
    | 'name'
    | 'spdx_id'
    | 'commercial_use'
    | 'redistribution'
    | 'share_alike'
    | 'caching_limit_seconds'
    | 'attribution_text'
    | 'attribution_required'
    | 'terms_url'
    | 'checked_at'
    | 'checked_by'
    | 'notes'
  >
>

export interface Run {
  id: number
  kind: string
  target_kind: string
  target_slug: string
  status: string
  trigger: string
  created_by: string
  created_at: string
  started_at: string | null
  finished_at: string | null
  rows_in: number | null
  rows_out: number | null
  quarantined: number
  error: string | null
  /** Live status while running, e.g. {zone: "se", step: "transfer", records: 120000}. */
  progress: Record<string, unknown> | null
  cancel_requested: boolean
  worker: string | null
  input_versions: Record<string, unknown>
  output_version: string | null
  schema_fingerprint: string | null
  prev_fingerprint: string | null
  /** Extraction runs that failed on a layout change: per template, what changed. */
  fingerprint_diff?: Record<string, FingerprintDiff> | null
}

export interface RunLogEntry {
  at: string
  level: 'info' | 'warn' | 'error'
  msg: string
  [field: string]: unknown
}

export interface ZoneTransfer {
  zone: string
  checked_at: string
  soa_serial: number | null
  transferred: boolean
  outcome: string
  bytes: number | null
  records: number | null
  duration_ms: number | null
  error: string | null
  run_id: number | null
}

export interface ZoneSnapshot {
  zone: string
  serial: number
  previous_serial: number | null
  fetched_at: string
  domains: number
  events: { added?: number; removed?: number; changed_ns?: number; changed_dnssec?: number; baseline?: boolean }
  snapshot_key: string
  events_key: string
  snapshot_deleted_at: string | null
  run_id: number | null
}

/** A preview run's result: a dry-run build of a draft spec. */
export interface PreviewResult {
  rows_in: number
  ok: number
  quarantined: number
  final: number
  by_rule: Record<string, number>
  columns: string[]
  rows: Record<string, unknown>[]
  quarantine: Record<string, unknown>[]
  note?: string
}

export interface RunDetail extends Run {
  /** Preview runs: the dry-run's counts and sample. */
  result?: PreviewResult | null
  log: RunLogEntry[]
  transfers: ZoneTransfer[]
  snapshots: ZoneSnapshot[]
  /** Build runs: the output and the first quarantined rows. */
  build: Build | null
  quarantine: QuarantineRow[]
  quarantine_rules: RuleCount[]
}

export interface Column {
  name: string
  type: string
  description?: string
}

export interface Effective {
  sources: string[]
  /** Sources published without attribution: they restrict, but are not listed in API responses. */
  undisclosed: string[]
  licenses: string[]
  attribution: string[]
  usage_mode: string
  share_alike: boolean
  commercial_use: boolean | null
  redistribution: boolean | null
  caching_limit_seconds: number | null
  complete: boolean
  findings: Findings
}

/** One successful build (meta.bucket_meta). */
export interface Build {
  run_id: number
  built_at: string
  data_fetched_at: string
  rows: number
  bytes: number
  quarantined: number
  output_key: string | null
  spec_version: number | null
  serve_table: string | null
  output_deleted_at: string | null
  effective_license: Effective
  sources: { slug: string; name: string; publisher: string; url: string }[]
}

export interface BucketRow {
  slug: string
  title: string
  description: string
  columns: Column[]
  storage: 'parquet' | 'postgres' | 'both'
  exportable: boolean
  schedule: string | null
  ttl_seconds: number
  enabled: boolean
  spec_version: number
  last_run_id: number | null
  last_success_run_id: number | null
  created_at: string
  updated_at: string
  last_build: Build | null
  last_run_status: string | null
  last_run_at: string | null
  active_run: Run | null
  inputs: string[]
  license: Effective | null
}

export interface SpecVersion {
  version: number
  body: Record<string, unknown>
  comment: string
  created_at: string
  created_by: string
}

export interface LineageRow {
  field: string
  input_kind: string
  input_slug: string
  input_field: string
}

export interface RuleCount {
  rule: string
  reason: string
  rows: number
}

export interface QuarantineRow {
  run_id: number
  rule: string
  reason: string
  row: Record<string, unknown>
}

export interface BucketDetail extends BucketRow {
  spec: SpecVersion | null
  versions: SpecVersion[]
  lineage: LineageRow[]
  through_buckets: string[]
  findings: Findings
  runs: Run[]
  builds: Build[]
  quarantine: RuleCount[]
  dependents: string[]
}

export type BucketPatch = Partial<Pick<BucketRow, 'title' | 'description' | 'storage' | 'exportable' | 'schedule' | 'ttl_seconds' | 'enabled'>>

/** Result of validating a spec: blocking problems, licence findings, the compiled SQL. */
export interface SpecReport {
  ok: boolean
  problems: string[]
  findings: Findings
  effective: Effective | null
  lineage: LineageRow[]
  sql: string[]
}

export interface Sample {
  table: string | null
  total: number | null
  rows: Record<string, unknown>[]
}

export interface SourceTable {
  slug: string
  source: string
  description: string
  columns: Column[]
  location: string
  produced_by: string
  updated_at: string
  objects: number
  rows: number | null
  latest_at: string | null
}

export interface EndpointReport {
  ok: boolean
  problems: string[]
  findings: Findings
  effective: Effective | null
  example: string
}

export interface EndpointRow {
  slug: string
  org_id: string
  /** Also an MCP tool while published (milestone 13). */
  mcp_enabled: boolean
  title: string
  status: 'draft' | 'published' | 'archived'
  current_version: number
  published_version: number | null
  published_at: string | null
  /** Published on the sandbox (dev.aleva.se), switched separately. */
  dev_published_version: number | null
  dev_published_at: string | null
  updated_at: string
  path: string
  bucket: string
  published_path: string | null
  gate: EndpointReport
  requests_7d: number
}

/** A draft endpoint run through the gateway's request code (nothing saved). */
export interface EndpointPreviewResult {
  gate: EndpointReport
  /** 0 when the spec has problems and nothing ran. */
  status: number
  request?: string
  error?: string
  sql?: string
  data: unknown[]
  more: boolean
}

export interface EndpointDetail extends EndpointRow {
  versions: SpecVersion[]
  usage: { day: string; keyed: number; anonymous: number }[]
}

export interface PageReport {
  ok: boolean
  problems: string[]
  findings: Findings
  endpoints: { slug: string; path: string; published: boolean }[]
}

export interface PageRow {
  kind: 'showcase' | 'product'
  slug: string
  title: string
  status: 'draft' | 'published' | 'archived'
  current_version: number
  published_version: number | null
  published_at: string | null
  /** Published on the sandbox (dev.aleva.se), switched separately. */
  dev_published_version: number | null
  dev_published_at: string | null
  updated_at: string
  path: string
  gate: PageReport
  versions?: SpecVersion[]
}

export interface Plan {
  slug: string
  name: string
  requests_per_minute: number
  requests_per_day: number
  price_note: string
  public: boolean
}

export interface ApiKey {
  id: number
  prefix: string
  kind: string
  plan: string
  owner_email: string
  label: string
  created_at: string
  created_by: string
  revoked_at: string | null
  last_used_at: string | null
  requests_30d: number
}

export interface Signup {
  id: number
  email: string
  plan: string
  page: string
  lang: string
  note: string
  created_at: string
}

/** Where the public API answers (for "try it" links). */
export const publicApiUrl = location.hostname.endsWith('aleva.se') ? 'https://api.aleva.se' : 'http://localhost:8094'

export interface BucketStatus {
  slug: string
  enabled: boolean
  built_at: string | null
  data_fetched_at: string | null
  rows: number | null
  last_run_status: string | null
}

export interface WorkerStatus {
  name: string
  started_at: string
  heartbeat_at: string
  leader: boolean
  version: string
  current_run_id: number | null
}

export interface ZoneStatus {
  zone: string
  serial: number | null
  fetched_at: string | null
  domains: number | null
  last_check_at: string | null
  last_outcome: string | null
}

export interface Status {
  now: string
  worker_ok: boolean
  workers: WorkerStatus[]
  running: Run[]
  queued: Run[]
  zones: ZoneStatus[]
  buckets: BucketStatus[]
}

export interface SourceDetail {
  slug: string
  name: string
  publisher: string
  url: string
  description: string
  access_method: string
  usage_mode: string
  schedule: string | null
  min_interval_seconds: number | null
  status: string
  config: Record<string, unknown>
  created_at: string
  updated_at: string
  next_run: string | null
  licenses: License[]
  license_findings: Findings
  runs: Run[]
  transfers: ZoneTransfer[]
  snapshots: ZoneSnapshot[]
}

export type SourcePatch = Partial<
  Pick<SourceDetail, 'name' | 'publisher' | 'url' | 'description' | 'usage_mode' | 'schedule' | 'min_interval_seconds' | 'status'>
>

export interface AuditEntry {
  id: number
  at: string
  actor_name: string
  action: string
  entity_kind: string
  entity_slug: string
  version: number | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
}

// A row of the unified activity feed: a change (audit) or a data access.
export interface ActivityRow {
  type: 'change' | 'access'
  at: string
  actor_kind: 'user' | 'api_key' | 'anonymous' | 'system' | 'agent'
  actor_name: string
  action: string
  object_kind: string
  object_slug: string
  org_id: string | null
  client_ip: string
  version: number | null // change
  change_set_id: number | null // change
  api_key_id: number | null // access
  rows: number | null // access
  keys: string[] // access
  status: number | null // access
  params: Record<string, unknown> | null // access
  after: Record<string, unknown> | null // change
}

// A row of the Aleva MCP registry (milestone 16).
export interface MCPServer {
  id: number
  url: string
  domain: string
  tld: string
  name: string
  description: string
  transport: string
  source: 'crawl' | 'submitted'
  status: 'pending' | 'verified' | 'approved' | 'rejected'
  tool_count: number | null
  created_at: string
  approved_by: string | null
  approved_at: string | null
}

// Global domain blocklist: domains we must never query (a site asked us to
// stop, or a legal/abuse reason). An entry covers the domain and all subdomains.
export interface BlockedDomain {
  domain: string
  reason: string
  requested_by: string
  source: 'request' | 'legal' | 'abuse' | 'manual'
  created_at: string
  created_by: string
}

export interface BlockedDomainInput {
  domain: string
  reason: string
  requested_by: string
  source: BlockedDomain['source']
}

// Form builder (milestone 17).
export interface FormRow {
  slug: string
  status: 'draft' | 'published' | 'archived'
  current_version: number
  published_version: number | null
  /** Published on the sandbox (dev.aleva.se), switched separately. */
  dev_published_version: number | null
}

export interface FormSubmission {
  id: number
  form_slug: string
  data: Record<string, string>
  status: 'pending' | 'verified' | 'approved' | 'rejected' | 'active'
  requirements_met: string[]
  created_at: string
  activated_at: string | null
  reviewed_by: string | null
}

export interface ActivityParams {
  type?: string
  actor?: string
  action?: string
  object_kind?: string
  object_slug?: string
  key?: string
  from?: string
  to?: string
  all?: string
  sort?: string
  dir?: string
  limit?: number
  offset?: number
}

// Extraction and integrations (milestone 6) ---------------------------------

export interface Integration {
  org_id: string
  slug: string
  kind: 'http' | 'google_drive'
  name: string
  description: string
  enabled: boolean
  config: Record<string, unknown>
  /** What identifies the stored credential (last characters, service account); never the secret. */
  secret_hint: string
  enabled_by: string | null
  enabled_at: string | null
  created_by: string
  created_at: string
  updated_at: string
  sources: string[]
}

export interface IntegrationList {
  integrations: Integration[]
  /** Credentials can be stored (a key-encryption key is configured). */
  sealing_enabled: boolean
  /** The key-encryption key wrapping the org keys, e.g. "transit:transit/aleva-org-keys" or "local". */
  kek: string
}

export interface IntegrationInput {
  slug?: string
  kind?: 'http' | 'google_drive'
  name?: string
  description?: string
  config?: Record<string, unknown>
  /** Write-only: a new credential ("" removes it). */
  secret?: string
}

export interface SourceInput {
  slug: string
  name: string
  publisher: string
  url: string
  description?: string
  access_method: string
  usage_mode: string
  schedule?: string | null
  config?: Record<string, unknown>
}

export interface RawFile {
  id: number
  key: string
  sha256: string
  bytes: number
  content_type: string
  upstream_version: string | null
  original_name: string | null
  uploaded_by: string | null
  fetched_at: string
  run_id: number | null
}

/** A merged cell range, 0-based and inclusive. */
export interface Merge {
  from_row: number
  from_col: number
  to_row: number
  to_col: number
}

export interface GridSheet {
  name: string
  rows: number
  cols: number
  cells?: string[][]
  merges?: Merge[]
}

export interface Grid {
  file: RawFile
  format: string
  sheets: GridSheet[]
  sheet: number
}

export interface ExtractColumn {
  source: string
  name: string
  type: string
  nulls?: string[]
}

export interface SkipRule {
  kind: 'blank' | 'first_cell_matches' | 'any_cell_matches' | 'row_index'
  pattern?: string
  rows?: number[]
}

export interface ExtractionSpec {
  format: 'xlsx' | 'csv' | 'json' | 'xml'
  source: string
  output_table: string
  sheet?: string | number
  range?: string
  header_rows: number[]
  header_joiner: string
  orientation: 'vertical' | 'horizontal'
  fill_merged: boolean
  skip?: SkipRule[]
  null_markers: string[]
  record_path?: string
  csv_delimiter?: string
  csv_encoding: string
  columns: ExtractColumn[]
  expected_fingerprint?: string
  max_cast_failures: number
}

export interface Template {
  slug: string
  version: number
  spec: ExtractionSpec
  comment: string
  created_by: string
  created_at: string
}

export interface TemplateVersion {
  version: number
  comment: string
  created_by: string
  created_at: string
}

export interface ColumnStats {
  name: string
  type: string
  source_index: number
  header: string
  nulls: number
  cast_failed: number
  cast_failed_examples?: string[]
}

export interface FingerprintDiff {
  expected: string
  actual: string
  missing?: string[]
  headers: string[]
}

export interface Preview {
  sheet: string
  headers: string[]
  header_fingerprint: string
  schema_fingerprint: string
  columns: ColumnStats[]
  rows_in: number
  skipped: number
  problems?: string[]
  fingerprint_diff?: FingerprintDiff
  rows: Record<string, unknown>[]
  file: RawFile
}

export const columnTypes = ['VARCHAR', 'BIGINT', 'INTEGER', 'DOUBLE', 'BOOLEAN', 'DATE', 'TIMESTAMP', 'TIMESTAMPTZ', 'VARCHAR[]', 'INTEGER[]', 'BIGINT[]', 'JSON']

/** Access methods whose files are read with extraction templates. */
export const templateMethods = ['upload', 'http', 'google_drive']

async function upload<T>(path: string, file: File): Promise<T> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`/admin-api${path}`, { method: 'POST', credentials: 'same-origin', body: fd })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiError(res.status, typeof data.detail === 'string' ? data.detail : `${res.status} ${res.statusText}`, data.findings)
  return data as T
}

// CEL expressions (milestone 7) -------------------------------------------

/** Where an expression sits in a build spec. */
export interface ExprTarget {
  kind: 'field' | 'where' | 'group_by' | 'rule' | 'unnest'
  input?: string
}

export interface CelDiagnostic {
  /** Code-point offsets into the expression. */
  start: number
  end: number
  line: number
  column: number
  message: string
}

export interface ExprCheck {
  ok: boolean
  type?: string
  sql_type?: string
  sql?: string
  aggregate: boolean
  diagnostics: CelDiagnostic[]
  variables: { name: string; type: string }[]
}

export interface CelFunction {
  name: string
  signature: string
  doc: string
  example?: string
  member: boolean
  aggregate: boolean
  group: string
}

export class ApiError extends Error {
  status: number
  findings?: Findings
  constructor(status: number, message: string, findings?: Findings) {
    super(message)
    this.status = status
    this.findings = findings
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await send(method, path, body)
  return res.json() as Promise<T>
}

/** A request whose answer is not JSON (SVG, PNG, PDF). */
export async function requestRaw(method: string, path: string, body?: unknown): Promise<Response> {
  return send(method, path, body)
}

async function send(method: string, path: string, body?: unknown): Promise<Response> {
  const headers: Record<string, string> = body === undefined ? {} : { 'Content-Type': 'application/json' }
  const org = activeOrg.get()
  if (org) headers['X-Aleva-Org'] = org
  const res = await fetch(`/admin-api${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`
    let findings: Findings | undefined
    try {
      const data = await res.json()
      if (typeof data.detail === 'string') message = data.detail
      else if (Array.isArray(data.detail)) message = data.detail.map((d: { loc: string[]; msg: string }) => `${d.loc.slice(1).join('.')}: ${d.msg}`).join('; ')
      findings = data.findings
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, message, findings)
  }
  return res
}

function query(params: Record<string, string | number | undefined>): URLSearchParams {
  return new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))
}

export const isActive = (r: Pick<Run, 'status'> | null | undefined) => r?.status === 'queued' || r?.status === 'running'

export const api = {
  me: () => request<Me>('GET', '/me'),
  // Customer login (app.aleva.se) and organisations.
  authConfig: () => request<{ oidc: boolean; email_links: boolean }>('GET', '/auth/config'),
  login: (email: string) => request<{ ok: boolean }>('POST', '/auth/login', { email }),
  loginCallback: (token: string) => request<{ ok: boolean; email: string }>('POST', '/auth/callback', { token }),
  acceptInvite: (token: string) => request<{ ok: boolean; email: string }>('POST', '/auth/accept', { token }),
  logout: () => request<{ ok: boolean }>('POST', '/auth/logout', {}),
  org: () => request<{ org: Org; members: Member[]; invitations: Invitation[] }>('GET', '/org'),
  invite: (email: string, role: string) => request<Invitation>('POST', '/org/invitations', { email, role }),
  revokeInvite: (id: number) => request<{ ok: boolean }>('POST', `/org/invitations/${id}/revoke`, {}),
  setMemberRole: (user: number, role: string) => request<{ ok: boolean }>('PATCH', `/org/members/${user}`, { role }),
  removeMember: (user: number) => request<{ ok: boolean }>('DELETE', `/org/members/${user}`),
  orgs: () => request<(Org | Membership)[]>('GET', '/orgs'),
  createOrg: (id: string, name: string, admin_email: string) =>
    request<{ org: Org; invitation: Invitation }>('POST', '/orgs', { id, name, admin_email }),
  setOrgDisabled: (id: string, disabled: boolean) => request<Org>('POST', `/orgs/${encodeURIComponent(id)}/${disabled ? 'disable' : 'enable'}`, {}),
  sources: () => request<SourceRow[]>('GET', '/sources'),
  source: (slug: string) => request<SourceDetail>('GET', `/sources/${encodeURIComponent(slug)}`),
  patchSource: (slug: string, patch: SourcePatch) =>
    request<SourceDetail>('PATCH', `/sources/${encodeURIComponent(slug)}`, patch),
  addLicense: (slug: string, input: LicenseInput) =>
    request<SourceDetail>('POST', `/sources/${encodeURIComponent(slug)}/licenses`, input),
  snapshotTerms: (slug: string) =>
    request<SourceDetail>('POST', `/sources/${encodeURIComponent(slug)}/licenses/snapshot-terms`),
  status: () => request<Status>('GET', '/status'),
  triggerRun: (slug: string) => request<Run>('POST', `/sources/${encodeURIComponent(slug)}/runs`),
  runs: (params: { kind?: string; target_kind?: string; target_slug?: string; status?: string; limit?: number } = {}) =>
    request<Run[]>('GET', `/runs?${query(params)}`),
  run: (id: number) => request<RunDetail>('GET', `/runs/${id}`),
  cancelRun: (id: number) => request<Run>('POST', `/runs/${id}/cancel`),
  audit: (params: { kind?: string; slug?: string; limit?: number } = {}) => request<AuditEntry[]>('GET', `/audit?${query(params)}`),
  activity: (params: ActivityParams = {}) => request<{ rows: ActivityRow[]; has_more: boolean }>('GET', `/activity?${query({ ...params })}`),
  activityCsv: (params: ActivityParams = {}) => requestRaw('GET', `/activity?${query({ ...params, format: 'csv' })}`).then((r) => r.blob()),
  forms: () => request<FormRow[]>('GET', '/forms'),
  publishForm: (slug: string) => request<{ slug: string; published: boolean }>('POST', `/forms/${encodeURIComponent(slug)}/publish`, {}),
  unpublishForm: (slug: string) => request<{ slug: string; published: boolean }>('POST', `/forms/${encodeURIComponent(slug)}/unpublish`, {}),
  publishFormDev: (slug: string, version = 0) => request<{ slug: string }>('POST', `/forms/${encodeURIComponent(slug)}/publish-dev`, { version }),
  unpublishFormDev: (slug: string) => request<{ slug: string }>('POST', `/forms/${encodeURIComponent(slug)}/unpublish-dev`),
  formSubmissions: (slug: string, status = '') => request<FormSubmission[]>('GET', `/forms/${encodeURIComponent(slug)}/submissions?${query({ status })}`),
  approveSubmission: (id: number) => request<{ id: number }>('POST', `/form-submissions/${id}/approve`, {}),
  rejectSubmission: (id: number) => request<{ id: number }>('POST', `/form-submissions/${id}/reject`, {}),
  mcpServers: (status = '') => request<MCPServer[]>('GET', `/mcp-servers?${query({ status })}`),
  approveMCPServer: (id: number) => request<MCPServer>('POST', `/mcp-servers/${id}/approve`, {}),
  rejectMCPServer: (id: number) => request<MCPServer>('POST', `/mcp-servers/${id}/reject`, {}),
  blocklist: () => request<BlockedDomain[]>('GET', '/blocklist'),
  addBlocked: (b: BlockedDomainInput) => request<{ domain: string }>('POST', '/blocklist', b),
  removeBlocked: (domain: string) => request<{ domain: string }>('DELETE', `/blocklist/${encodeURIComponent(domain)}`),
  buckets: () => request<BucketRow[]>('GET', '/buckets'),
  bucket: (slug: string) => request<BucketDetail>('GET', `/buckets/${encodeURIComponent(slug)}`),
  patchBucket: (slug: string, patch: BucketPatch) => request<BucketDetail>('PATCH', `/buckets/${encodeURIComponent(slug)}`, patch),
  saveSpec: (slug: string, spec: unknown, comment: string) =>
    request<BucketDetail>('POST', `/buckets/${encodeURIComponent(slug)}/spec`, { spec, comment }),
  validateSpec: (slug: string, spec: unknown) => request<SpecReport>('POST', `/buckets/${encodeURIComponent(slug)}/spec/validate`, { spec }),
  sample: (slug: string, limit = 50) => request<Sample>('GET', `/buckets/${encodeURIComponent(slug)}/sample?limit=${limit}`),
  triggerBuild: (slug: string) => request<Run>('POST', `/buckets/${encodeURIComponent(slug)}/runs`),
  sourceTables: () => request<SourceTable[]>('GET', '/source-tables'),
  endpoints: () => request<EndpointRow[]>('GET', '/endpoints'),
  endpoint: (slug: string) => request<EndpointDetail>('GET', `/endpoints/${encodeURIComponent(slug)}`),
  createEndpoint: (slug: string, spec: unknown, comment: string) => request<EndpointDetail>('POST', '/endpoints', { slug, spec, comment }),
  saveEndpoint: (slug: string, spec: unknown, comment: string) =>
    request<EndpointDetail>('POST', `/endpoints/${encodeURIComponent(slug)}/spec`, { spec, comment }),
  validateEndpoint: (slug: string, spec: unknown) => request<EndpointReport>('POST', `/endpoints/${encodeURIComponent(slug)}/validate`, { spec }),
  publishEndpoint: (slug: string, version?: number) =>
    request<EndpointDetail>('POST', `/endpoints/${encodeURIComponent(slug)}/publish`, { version: version ?? 0 }),
  setEndpointMCP: (slug: string, enabled: boolean) => request<EndpointDetail>('POST', `/endpoints/${encodeURIComponent(slug)}/mcp`, { enabled }),
  mcpPreview: (slug: string, spec: unknown) =>
    request<{ tool: { name: string; title: string; description: string; inputSchema: unknown }; bulk: boolean }>('POST', '/endpoints/mcp-preview', { slug, spec }),
  unpublishEndpoint: (slug: string) => request<EndpointDetail>('POST', `/endpoints/${encodeURIComponent(slug)}/unpublish`),
  previewEndpoint: (body: { slug?: string; spec: unknown; query: string; path?: Record<string, string> }) =>
    request<EndpointPreviewResult>('POST', '/endpoints/preview', body),
  publishEndpointDev: (slug: string, version?: number) =>
    request<EndpointDetail>('POST', `/endpoints/${encodeURIComponent(slug)}/publish-dev`, { version: version ?? 0 }),
  unpublishEndpointDev: (slug: string) => request<EndpointDetail>('POST', `/endpoints/${encodeURIComponent(slug)}/unpublish-dev`),
  plans: () => request<Plan[]>('GET', '/plans'),
  apiKeys: () => request<ApiKey[]>('GET', '/api-keys'),
  createKey: (input: { label: string; owner_email: string; plan: string; kind: string }) =>
    request<{ key: ApiKey; secret: string }>('POST', '/api-keys', input),
  revokeKey: (id: number) => request<ApiKey[]>('POST', `/api-keys/${id}/revoke`),
  signups: () => request<Signup[]>('GET', '/signups'),
  pages: () => request<PageRow[]>('GET', '/pages'),
  page: (kind: string, slug: string) => request<PageRow>('GET', `/pages/${kind}/${encodeURIComponent(slug)}`),
  savePage: (kind: string, slug: string, spec: unknown, comment: string) =>
    request<PageRow>('POST', `/pages/${kind}/${encodeURIComponent(slug)}/spec`, { spec, comment }),
  validatePage: (kind: string, slug: string, spec: unknown) =>
    request<PageReport>('POST', `/pages/${kind}/${encodeURIComponent(slug)}/validate`, { spec }),
  publishPage: (kind: string, slug: string) => request<PageRow>('POST', `/pages/${kind}/${encodeURIComponent(slug)}/publish`, { version: 0 }),
  unpublishPage: (kind: string, slug: string) => request<PageRow>('POST', `/pages/${kind}/${encodeURIComponent(slug)}/unpublish`),
  publishPageDev: (kind: string, slug: string) => request<PageRow>('POST', `/pages/${kind}/${encodeURIComponent(slug)}/publish-dev`, { version: 0 }),
  unpublishPageDev: (kind: string, slug: string) => request<PageRow>('POST', `/pages/${kind}/${encodeURIComponent(slug)}/unpublish-dev`),
  purgeCache: (body: { keys?: string[]; all?: boolean }) => request<{ ok: boolean }>('POST', '/cache/purge', body),
  publishReady: () =>
    request<{ published: { kind: string; slug: string }[]; refused: { kind: string; slug: string; reason: string }[] }>('POST', '/publish-ready', {}),
  createSource: (input: SourceInput) => request<SourceDetail>('POST', '/sources', input),
  integrations: () => request<IntegrationList>('GET', '/integrations'),
  createIntegration: (input: IntegrationInput) => request<Integration>('POST', '/integrations', input),
  patchIntegration: (slug: string, input: IntegrationInput) => request<Integration>('PATCH', `/integrations/${encodeURIComponent(slug)}`, input),
  deleteIntegration: (slug: string) =>
    fetch(`/admin-api/integrations/${encodeURIComponent(slug)}`, { method: 'DELETE', credentials: 'same-origin' }).then(async (r) => {
      if (!r.ok) throw new ApiError(r.status, (await r.json().catch(() => ({}))).detail ?? r.statusText)
    }),
  setIntegrationEnabled: (slug: string, on: boolean) =>
    request<Integration>('POST', `/integrations/${encodeURIComponent(slug)}/${on ? 'enable' : 'disable'}`),
  /** Queues a "test connection" run in the worker (the only service that can decrypt). */
  testIntegration: (slug: string) => request<Run>('POST', `/integrations/${encodeURIComponent(slug)}/test`),
  files: (source: string) => request<RawFile[]>('GET', `/sources/${encodeURIComponent(source)}/files`),
  uploadFile: (source: string, file: File) =>
    upload<{ file: RawFile | null; unchanged: boolean; run?: Run }>(`/sources/${encodeURIComponent(source)}/files`, file),
  grid: (source: string, fileId: number, params: { sheet?: string | number; format?: string; csv_delimiter?: string; csv_encoding?: string; record_path?: string }) =>
    request<Grid>('GET', `/sources/${encodeURIComponent(source)}/files/${fileId}/grid?${query(params)}`),
  sourceTemplates: (source: string) => request<Template[]>('GET', `/sources/${encodeURIComponent(source)}/templates`),
  template: (slug: string) => request<{ template: Template; versions: TemplateVersion[] }>('GET', `/templates/${encodeURIComponent(slug)}`),
  saveTemplate: (spec: ExtractionSpec, comment: string) => request<Template>('POST', '/templates', { spec, comment }),
  previewTemplate: (spec: ExtractionSpec, fileId: number) => request<Preview>('POST', '/templates/preview', { spec, file_id: fileId }),
  triggerExtract: (source: string) => request<Run>('POST', `/sources/${encodeURIComponent(source)}/extract`),
  checkExpression: (bucket: string, spec: unknown, target: ExprTarget, expression: string) =>
    request<ExprCheck>('POST', `/buckets/${encodeURIComponent(bucket)}/spec/check-expression`, { spec, target, expression }),
  previewSpec: (bucket: string, spec: unknown) => request<Run>('POST', `/buckets/${encodeURIComponent(bucket)}/spec/preview`, { spec }),
  celFunctions: () => request<{ functions: CelFunction[]; types: string[] }>('GET', '/cel-functions'),
}

/** Where the public site answers (links from the pages manager). */
export const publicSiteUrl = location.hostname.endsWith('aleva.se') ? 'https://aleva.se' : 'http://localhost:8092'

export function can(me: Me | undefined, role: 'viewer' | 'editor' | 'admin'): boolean {
  return !!me?.roles.includes(role)
}
